import { useState, useCallback, useEffect, useRef } from 'react';

import { FolderOpen, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/api-client';
import { useEmbedStore } from '@/stores/embed-store';

import type { ResourcePickerConfig, SelectedResource, ConnectionAccessResponse } from '@optidev/shared-types';

// Google Picker API types
declare global {
  interface Window {
    google?: {
      picker: {
        PickerBuilder: new () => GooglePickerBuilder;
        DocsView: new (viewId?: string) => GoogleDocsView;
        ViewId: {
          SPREADSHEETS: string;
          DOCS: string;
          FOLDERS: string;
        };
        Feature: {
          NAV_HIDDEN: string;
          MULTISELECT_ENABLED: string;
        };
        Action: {
          PICKED: string;
          CANCEL: string;
        };
      };
    };
    gapi?: {
      load: (api: string, config: { callback: () => void }) => void;
    };
  }
}

interface GooglePickerBuilder {
  setAppId(appId: string): GooglePickerBuilder;           // Google Cloud Project Number
  setDeveloperKey(key: string): GooglePickerBuilder;      // Google Picker API Key
  setOAuthToken(token: string): GooglePickerBuilder;      // OAuth access token
  setOrigin(origin: string): GooglePickerBuilder;
  addView(view: GoogleDocsView): GooglePickerBuilder;
  setCallback(callback: (data: GooglePickerResponse) => void): GooglePickerBuilder;
  setTitle(title: string): GooglePickerBuilder;
  enableFeature(feature: string): GooglePickerBuilder;
  setMaxItems(max: number): GooglePickerBuilder;
  build(): GooglePicker;
}

interface GoogleDocsView {
  setMimeTypes(mimeTypes: string): GoogleDocsView;
  setIncludeFolders(include: boolean): GoogleDocsView;
  setSelectFolderEnabled(enabled: boolean): GoogleDocsView;
}

interface GooglePicker {
  setVisible(visible: boolean): void;
}

interface GooglePickerResponse {
  action: string;
  docs?: Array<{
    id: string;
    name: string;
    url: string;
    mimeType: string;
  }>;
}

interface GooglePickerProps {
  config: ResourcePickerConfig;
  accessToken?: string;
  connectionId?: string;  // For lazy token fetching
  getAuthToken?: () => Promise<string | null>;  // Firebase auth token getter
  onSelect: (resources: SelectedResource[]) => void;
  disabled?: boolean;
  buttonLabel?: string;
  currentSelectionCount?: number; // For auto-open feature
}

interface GrantAccessResult {
  success: boolean;
  failedFiles: string[];
  error?: string;
}

/**
 * Grant drive.file scope access to selected files by "opening" them via Drive API.
 *
 * IMPORTANT: The drive.file scope only grants access to files that the app has
 * "created or opened". Simply selecting a file in Google Picker does NOT count
 * as "opening" it. We must make a Drive API call to each file to grant access.
 *
 * @see https://developers.google.com/drive/api/guides/about-auth#drive_file
 */
async function grantDriveFileAccess(
  files: Array<{ id: string; name: string }>,
  accessToken: string,
): Promise<GrantAccessResult> {
  const failedFiles: string[] = [];

  const results = await Promise.allSettled(
    files.map(async (file) => {
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files/${file.id}?fields=id,name`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );

      if (!response.ok) {
        // Log error for debugging
        let errorBody: unknown = null;
        try {
          errorBody = await response.json();
        } catch {
          errorBody = await response.text().catch(() => null);
        }
        console.error(`[GooglePicker] Failed to access "${file.name}":`, response.status, errorBody);
        failedFiles.push(file.name);
        throw new Error(`Failed to access ${file.name}`);
      }

      return response.json();
    }),
  );

  const failures = results.filter((r) => r.status === 'rejected');

  if (failures.length > 0) {
    return {
      success: false,
      failedFiles,
      error: failedFiles.length === files.length
        ? 'Unable to access the selected files. Please try re-authenticating.'
        : `Unable to access ${failedFiles.length} file(s): ${failedFiles.join(', ')}`,
    };
  }

  return { success: true, failedFiles: [] };
}

// Environment variable for Google Picker App ID (Google Cloud Project Number)
const GOOGLE_PICKER_APP_ID = import.meta.env.VITE_GOOGLE_PICKER_APP_ID || '';

/**
 * GooglePicker - Google Picker API integration
 *
 * Opens as an overlay (not popup) - no popup blocker issues
 *
 * IMPORTANT: For drive.file scope to work, the picker MUST be configured with:
 * - setAppId(projectNumber) - Google Cloud Project Number (REQUIRED)
 * - setOAuthToken(token) - OAuth access token (REQUIRED)
 *
 * Without setAppId, the picker won't grant the OAuth token access to selected
 * files, resulting in 404 errors when trying to read them.
 *
 * @see https://developers.google.com/drive/picker/guides/overview
 */
export function GooglePicker({
  config,
  accessToken: accessTokenProp,
  connectionId,
  getAuthToken,
  onSelect,
  disabled,
  buttonLabel = 'Select File',
  currentSelectionCount = 0,
}: GooglePickerProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingToken, setIsFetchingToken] = useState(false);
  const [fetchedToken, setFetchedToken] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [accessError, setAccessError] = useState<string | null>(null);
  // Initialize based on whether picker is already available (from previous page load)
  const [isApiLoaded, setIsApiLoaded] = useState(() => !!window.google?.picker);
  const hasAutoOpened = useRef(false); // Track if we've auto-opened (don't repeat on re-renders)

  // Use prop token if available, otherwise use fetched token
  const accessToken = accessTokenProp || fetchedToken;

  // Load Google Picker API
  useEffect(() => {
    // Already loaded
    if (window.google?.picker) {
      if (!isApiLoaded) setIsApiLoaded(true);
      return;
    }

    const onPickerApiLoad = () => {
      setIsApiLoaded(true);
    };

    // Check if gapi is loaded but picker module isn't
    if (window.gapi) {
      window.gapi.load('picker', { callback: onPickerApiLoad });
      return;
    }

    // Check if script is already in DOM
    const existingScript = document.querySelector('script[src="https://apis.google.com/js/api.js"]');
    if (existingScript) {
      // Poll for gapi to be available
      const checkGapi = setInterval(() => {
        if (window.google?.picker) {
          clearInterval(checkGapi);
          setIsApiLoaded(true);
        } else if (window.gapi) {
          clearInterval(checkGapi);
          window.gapi.load('picker', { callback: onPickerApiLoad });
        }
      }, 100);
      return () => clearInterval(checkGapi);
    }

    // Load script fresh
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.gapi?.load('picker', { callback: onPickerApiLoad });
    };
    script.onerror = () => {
      console.error('[GooglePicker] Failed to load Google Picker API');
    };
    document.body.appendChild(script);
  }, [isApiLoaded]);

  const openPicker = useCallback((tokenOverride?: string) => {
    const token = tokenOverride || accessToken;

    if (!token || !window.google?.picker) {
      console.error('[GooglePicker] API not loaded or no access token');
      return;
    }

    setIsLoading(true);

    try {
      // Build MIME types - honor the connector's explicit config; when a connector
      // wants ALL file types (resourceType 'any', e.g. Google Drive) with no explicit
      // mimeTypes, leave the view unfiltered. Otherwise default to Sheets/Excel.
      const explicitMimeTypes = config.google?.mimeTypes?.join(',') || '';
      const mimeTypes =
        explicitMimeTypes ||
        (config.resourceType === 'any'
          ? '' // no filter → show all Drive files
          : 'application/vnd.google-apps.spreadsheet,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');

      // Title reflects the resource type so the picker isn't Sheets-specific
      const pickerTitle =
        config.resourceType === 'document'
          ? 'Select a Google Doc'
          : config.resourceType === 'presentation'
            ? 'Select a Google Slides presentation'
            : config.resourceType === 'folder'
              ? 'Select a folder'
              : config.resourceType === 'any'
                ? 'Select a file'
                : 'Select a Google Sheet';

      // Create DocsView (no viewId in constructor). Only apply a MIME filter when one
      // is set — an empty filter shows all Drive files (used by the Google Drive connector).
      const view = new window.google.picker.DocsView()
        .setIncludeFolders(true)
        .setSelectFolderEnabled(false);
      if (mimeTypes) {
        view.setMimeTypes(mimeTypes);
      }

      // Get App ID from config or environment variable
      const appId = config.google?.appId || GOOGLE_PICKER_APP_ID;

      // Validate required configuration
      if (!appId) {
        console.error('[GooglePicker] Missing required configuration:', {
          hasAppId: !!appId,
          hint: 'Set VITE_GOOGLE_PICKER_APP_ID environment variable',
        });
        setAccessError('Google Picker is not configured. Please contact support.');
        setIsLoading(false);
        return;
      }

      // When running inside an iframe (e.g. OptiSigns embed), Picker validates
      // setOrigin against the top-most page's origin, not the iframe's. Fall
      // back to window.location.origin in standalone mode.
      const { isEmbedded, parentOrigin } = useEmbedStore.getState();
      const pickerOrigin = isEmbedded && parentOrigin ? parentOrigin : window.location.origin;

      // Build picker with setAppId (required for drive.file scope)
      // @see https://developers.google.com/drive/picker/guides/overview
      const builder = new window.google.picker.PickerBuilder()
        .setAppId(appId)
        .setOAuthToken(token)
        .setOrigin(pickerOrigin)
        .addView(view)
        .setTitle(pickerTitle)
        .setCallback(async (data: GooglePickerResponse) => {
          // Clear any previous access errors
          setAccessError(null);

          if (data.action === window.google?.picker.Action.PICKED && data.docs) {
            // Keep loading state while granting access
            const files = data.docs.map((doc) => ({ id: doc.id, name: doc.name }));

            // Grant drive.file scope access by "opening" files via Drive API
            // This is required because drive.file only grants access to files
            // that the app has "created or opened" - picker selection alone doesn't count
            const result = await grantDriveFileAccess(files, token);

            if (!result.success) {
              // Show error to user and don't proceed with selection
              setAccessError(result.error || 'Unable to access the selected files.');
              setIsLoading(false);
              return;
            }

            const resources: SelectedResource[] = data.docs.map((doc) => ({
              id: doc.id,
              name: doc.name,
              url: doc.url,
              mimeType: doc.mimeType,
              provider: 'google' as const,
            }));
            onSelect(resources);
          }

          // Reset loading state after processing
          setIsLoading(false);
        });

      // Enable multi-select if configured
      if (config.multiple) {
        builder.enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED);
        if (config.maxSelection) {
          builder.setMaxItems(config.maxSelection);
        }
      }

      const picker = builder.build();
      picker.setVisible(true);
    } catch (error) {
      console.error('Error opening Google Picker:', error);
      setIsLoading(false);
    }
  }, [accessToken, config, onSelect]);

  // Auto-open picker when both accessToken AND API are ready
  // Only auto-open once, and only if no resources are selected yet
  useEffect(() => {
    // Skip if already auto-opened or conditions not met
    if (hasAutoOpened.current) return;
    if (!accessToken) return;
    if (!isApiLoaded) return;
    if (currentSelectionCount > 0) return;

    hasAutoOpened.current = true;

    // Small delay to ensure UI is ready
    setTimeout(() => {
      openPicker();
    }, 300);
  }, [accessToken, isApiLoaded, currentSelectionCount, openPicker]);

  // Fetch access token lazily when needed
  const fetchAccessToken = useCallback(async (): Promise<string | null> => {
    if (!connectionId || !getAuthToken) {
      return null;
    }

    try {
      setIsFetchingToken(true);
      setTokenError(null);
      const authToken = await getAuthToken();
      if (!authToken) {
        setTokenError('Authentication required');
        return null;
      }

      const { data: accessData } = await apiClient.post<ConnectionAccessResponse>(
        `/api/connections/${connectionId}/access`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      const token = accessData.data?.accessToken as string | undefined;
      if (token) {
        setFetchedToken(token);
        return token;
      }
      return null;
    } catch (error) {
      console.error('[GooglePicker] Failed to fetch access token:', error);
      // Check if it's a token refresh error
      const axiosError = error as { response?: { data?: { message?: string } } };
      const errorMessage = axiosError.response?.data?.message;
      if (errorMessage?.includes('refresh') || errorMessage?.includes('re-authenticate')) {
        setTokenError('Session expired. Please click Reauthorize above to reconnect.');
      } else {
        setTokenError('Failed to load. Please try again or click Reauthorize.');
      }
      return null;
    } finally {
      setIsFetchingToken(false);
    }
  }, [connectionId, getAuthToken]);

  const handleButtonClick = async () => {
    // Clear any previous errors
    setAccessError(null);

    // If no token available, try to fetch it first
    if (!accessToken && connectionId && getAuthToken) {
      const token = await fetchAccessToken();
      if (!token) return;
      // Pass token directly to avoid waiting for state update
      openPicker(token);
      return;
    }

    openPicker();
  };

  // Button is enabled if: API loaded AND (has token OR can fetch token lazily) AND no token error
  const canOpenPicker = isApiLoaded && (accessToken || (connectionId && getAuthToken)) && !tokenError;
  const isButtonLoading = isLoading || isFetchingToken;

  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="outline"
        onClick={handleButtonClick}
        disabled={disabled || isButtonLoading || !canOpenPicker}
        className="w-full"
      >
        {isButtonLoading ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <FolderOpen className="h-4 w-4 mr-2" />
        )}
        {isFetchingToken ? 'Loading...' : isLoading ? 'Opening...' : !isApiLoaded ? 'Loading...' : buttonLabel}
      </Button>
      {tokenError && (
        <p className="text-sm text-destructive">{tokenError}</p>
      )}
      {accessError && (
        <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20">
          <p className="text-sm text-destructive font-medium">Unable to access files</p>
          <p className="text-sm text-destructive/80 mt-1">{accessError}</p>
          <p className="text-xs text-muted-foreground mt-2">
            Try clicking "Reauthorize" above, or select different files.
          </p>
        </div>
      )}
    </div>
  );
}

export default GooglePicker;
